<template>
  <v-container class="page-view__container ma-0 pa-0" fluid v-on="$listeners">
    <div class="page-view__header">
      <slot name="header" />
    </div>

    <div
      class="page-view__content"
      :class="
        $route.name !== 'AnalyticsV3Resume'
          ? 'pa-5'
          : isMobileBreakpoint
          ? 'pr-0'
          : 'pr-3'
      "
      v-bind="$attrs"
    >
      <div
        v-if="$route.name !== 'AnalyticsV3Resume'"
        ref="tabsContainer" 
        class="tabs-container"
        data-position="right"
        :data-step="canViewTourInOthersPages ? 3 : undefined"
        :data-intro="
          canViewTourInOthersPages
            ? `<strong>${$t(
                'message.pages.analyticsV3.tours.plays.step3.title'
              )}</strong><br><br>${$t(
                'message.pages.analyticsV3.tours.plays.step3.description'
              )}`
            : undefined
        "
      >
        <div>
          <span class="body-label-caption neutral-60--text ma-4">{{
            $t('message.pages.analyticsV3.views')
          }}</span>

          <v-tabs
            v-model="currentRouteName"
            class="analytics-tabs page-view__tabs"
            :vertical="true"
            :show-arrows="false"
            color="primary"
            background-color="transparent"
          >
            <v-tab
              v-for="tab in tabs"
              :key="`${tab.name}-${tab.label}`"
              :class="{
                'v-tab--active': isActive(tab),
              }"
              :disabled="tab.inSoon"
              :to="!tab.inSoon ? { name: tab.name } : undefined"
              exact
              class="v-tab-text"
            >
              {{ tab.label }}

              <div v-if="tab.isNew" class="content-badge">
                <span
                  class="badge-text body-regular-smaller brand-20-darkest--text"
                >
                  {{ $i18next.t('message.newItemChip') }}
                </span>
              </div>

              <div v-if="tab.inSoon" class="content-badge">
                <span
                  class="badge-text body-regular-smaller brand-20-darkest--text"
                >
                  {{ $i18next.t('message.itemSoon') }}
                </span>
              </div>
            </v-tab>
          </v-tabs>
        </div>

        <div>
          <span class="body-label-caption neutral-60--text ma-4">{{
            $t('message.pages.analyticsV3.bandwidth')
          }}</span>

          <v-tabs
            v-model="currentRouteNameGeneralTab"
            class="analytics-tabs page-view__tabs"
            :vertical="true"
            :show-arrows="false"
            color="primary"
            background-color="transparent"
          >
            <v-tab
              v-for="tab in generalTabs"
              :key="`${tab.name}-${tab.label}`"
              :to="{ name: tab.name }"
              :class="{ 'v-tab--active': isActive(tab) }"
              exact
              class="v-tab-text"
            >
              {{ tab.label }}

              <div v-if="tab.isNew" class="content-badge">
                <span
                  class="badge-text body-regular-smaller brand-20-darkest--text"
                >
                  {{ $i18next.t('message.newItemChip') }}
                </span>
              </div>

              <div v-if="tab.inSoon" class="content-badge">
                <span
                  class="badge-text body-regular-smaller brand-20-darkest--text"
                >
                  {{ $i18next.t('message.itemSoon') }}
                </span>
              </div>
            </v-tab>
          </v-tabs>
        </div>
      </div>

      <div
        class="page-view__slot"
        :class="{
          'page-view__slot__isNotResume':
            $route.name !== 'AnalyticsV3Resume' &&
            $route.name !== 'AnalyticsV3VideoComparer',
        }"
      >
        <div class="page-view__content_slot">
          <slot name="content" />
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import {
  HAS_VIEWED_NEW_USERS_ANALYTICS,
  HAS_VIEWED_NEW_BANDWIDTH_ANALYTICS,
} from '@/config/envs.js'
import Storage from '@/utils/Storage'
import { differenceInCalendarDays, isSameDay, subDays } from 'date-fns'

export default {
  name: 'PageView',
  data: () => ({
    currentRouteName: '',
    currentRouteNameGeneralTab: '',
    hasViewedNewUsers: false,
    hasViewedNewBandwidth: false,
  }),
  computed: {
    ...mapState('user', {
      userPlanInfo: 'userPlanInfo',
    }),
    ...mapState({
      dateRange: (state) => state.analytics.filters.dateRange,
    }),
    tabs() {
      return [
        {
          name: 'AnalyticsV3Plays',
          label: this.$t('message.pages.analyticsV3.plays'),
          isNew: false,
        },
        {
          name: 'AnalyticsV3Retention',
          label: this.$t('message.pages.analyticsV3.retention'),
          isNew: false,
        },
        {
          name: 'AnalyticsV3Devices',
          label: this.$t('message.pages.analyticsV3.devices'),
          isNew: false,
        },
        {
          name: 'AnalyticsV3Browsers',
          label: this.$t('message.pages.analyticsV3.browsers'),
          isNew: false,
        },
        {
          name: 'AnalyticsV3Locations',
          label: this.$t('message.pages.analyticsV3.locations'),
          isNew: false,
        },
        {
          name: 'AnalyticsV3Reference',
          label: this.$t('message.pages.analyticsV3.references'),
          isNew: false,
        },
        {
          name: 'AnalyticsV3UTM',
          label: this.$t('message.pages.analyticsV3.utm'),
          inSoon: true,
        },
        {
          name: 'AnalyticsV3Lives',
          label: this.$t('message.pages.analyticsV3.lives'),
          inSoon: true,
        },
        {
          name: 'AnalyticsV3BestTimes',
          label: this.$t('message.pages.analyticsV3.besttimes'),
          inSoon: true,
        },
        {
          name: 'AnalyticsV3Users',
          label: this.$t('message.pages.analyticsV3.users'),
          isNew: !this.hasViewedNewUsers,
        },
      ]
    },
    generalTabs() {
      return [
        {
          name: 'AnalyticsV3Bandwidth',
          label: this.$t('message.pages.analyticsV3.general'),
          isNew: !this.hasViewedNewBandwidth,
        },
      ]
    },
    canViewTourInOthersPages() {
      return (
        this.$route.name !== 'AnalyticsV3VideoComparer' &&
        this.$route.name !== 'AnalyticsV3Resume'
      )
    },
  },
  methods: {
    ...mapMutations('analytics', [
      'UPDATE_ERROR_STATE',
      'UPDATE_FILTER_DATA_RANGE',
    ]),
    ...mapState({
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    isActive(tab) {
      return this.$route.name === tab.name
    },
  },
  watch: {
    $route: {
      handler(newVal) {
        this.currentRouteName = newVal.name
        this.currentRouteNameGeneralTab = newVal.name

        if (
          newVal.name === 'AnalyticsV3Bandwidth' &&
          !this.hasViewedNewBandwidth
        ) {
          Storage.setItem(HAS_VIEWED_NEW_BANDWIDTH_ANALYTICS, true)
          this.hasViewedNewBandwidth = true
        }

        if (newVal.name === 'AnalyticsV3Users' && !this.hasViewedNewUsers) {
          Storage.setItem(HAS_VIEWED_NEW_USERS_ANALYTICS, true)
          this.hasViewedNewUsers = true
        }

        const sub = this.userPlanInfo?.subscription
        if (sub) {
          const [start, end] = [this.dateRange.start, this.dateRange.end]
          const cStart = new Date(sub.current_period_start * 1000)
          const cEnd = new Date(sub.current_period_end * 1000)

          if (isSameDay(start, cStart) && isSameDay(end, cEnd)) {
            this.UPDATE_FILTER_DATA_RANGE({
              start: subDays(new Date(), 31),
              end: subDays(new Date(), 1),
            })
            return
          }

          const len = differenceInCalendarDays(cEnd, cStart)
          const pStart = subDays(cStart, len + 1)
          const pEnd = subDays(cEnd, len + 1)
          if (isSameDay(start, pStart) && isSameDay(end, pEnd)) {
            this.UPDATE_FILTER_DATA_RANGE({
              start: subDays(new Date(), 31),
              end: subDays(new Date(), 1),
            })
            return
          }
        }

        this.UPDATE_ERROR_STATE(false)
      },
    },
    '$vuetify.theme.dark'() {
      this.$nextTick(() => {
        const scroller = this.$refs.tabsContainer
        if (!scroller) return
        scroller.style.overflowY = 'hidden'
        scroller.offsetHeight
        scroller.style.overflowY = 'auto'
      })
    }
  },
  mounted() {
    this.hasViewedNewUsers =
      Storage.getItem(HAS_VIEWED_NEW_USERS_ANALYTICS) ?? false
    this.hasViewedNewBandwidth =
      Storage.getItem(HAS_VIEWED_NEW_BANDWIDTH_ANALYTICS) ?? false
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_tabs';

.page-view {
  &__container {
    display: flex;
    flex-direction: column;
    height: 100%;
    background-color: varFigma(neutral-background);
  }

  /* header agora fixo no topo */
  &__header {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: varFigma(neutral-background);
  }

  &__content {
    flex: 1; /* ocupa o restante abaixo do header */
    display: flex;
    flex-direction: row;
    border-radius: 8px;
    overflow: hidden;
  }

  &__tabs {
    @include v-tabs($vertical: true);
  }

  &__tabs::v-deep {
    .v-tabs-slider-wrapper {
      display: none;
    }

    .v-tab {
      width: 230px;
      border: none !important;
      font-family: 'Rubik', sans-serif !important;
      font-size: 14px !important;
      font-weight: 500 !important;
      justify-content: space-between !important;

      &--active {
        color: varFigma(neutral-20-dark) !important;
        background-color: varFigma(neutral-95-lightest) !important;
      }

      &--disabled {
        opacity: 1;
      }

      &:not([class*='active']) {
        color: varFigma(neutral-30-base) !important;
      }
    }
  }
}

.not-sticky {
  position: static !important;
}

.tabs-container {
  position: fixed;
  width: 240px;
  overflow-y: none;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media (max-height: 600px), (max-width: 480px) {
  .tabs-container {
    top: 0;
    bottom: 0;
    height: auto;
  }

  .tabs-container:hover {
    overflow-y: auto;
  }
}

.page-view__slot {
  flex: 1;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  overflow-x: hidden;
}

.page-view__slot__isNotResume {
  padding-left: 250px;
}

.page-view__content_slot {
  width: 100%;
  height: 100%;
  padding-bottom: 64px;
  overflow: auto;
}

.analytics-tabs {
  max-width: 240px;
}

.bg-dark {
  .page-view {
    &__content {
      background-color: varFigma(--v-neutral-darken2);
      border-color: var(--v-neutral-darken1);
    }
  }
}

.v-tab-text {
  font-family: 'Rubik', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: varFigma(neutral-30-base);
  padding: 0.75rem !important;
  align-items: center;
}

.content-badge {
  display: flex;
  padding: 2px 4px;
  border-radius: 8px;
  background-color: varFigma(brand-90) !important;
  align-items: center;
}

.badge-text {
  font-size: 10px;
}

.tabs-container::-webkit-scrollbar {
  width: 4px;
  border-radius: 8px;
}

.tabs-container::-webkit-scrollbar-thumb {
  background-color: #d0d1d9 !important;
}

.theme--dark .tabs-container::-webkit-scrollbar-thumb {
  background-color: #52577a !important;
}
</style>
